import React, { ReactElement, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { SparklesIcon } from '../../atoms/icons/SparklesIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const Container = styled.View({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'flex-start',
  height: '100%',
})

const Tab = styled(PressableOpacity)<{ theme: ThemeType; isPanelOpen: boolean; tabColor: string }>(
  ({ theme: { spacing }, isPanelOpen, tabColor }) => ({
    height: '75px',
    width: isPanelOpen ? '75px' : '60px',
    borderTopLeftRadius: spacing['16px'],
    borderBottomLeftRadius: spacing['16px'],
    backgroundColor: tabColor,
    right: isPanelOpen ? '-15px' : 0,
    transition: '1s ease-in-out',
    padding: `${spacing['20px']} ${spacing['16px']}`,
  }),
)

const PanelContainer = styled.View<{
  theme: ThemeType
  isPanelOpen: boolean
  panelWidth: number
  panelContainerStyle?: CSSObject
  transitionTime: number
}>(({ theme: { colors, spacing }, isPanelOpen, panelWidth, panelContainerStyle, transitionTime }) => ({
  backgroundColor: colors.backgroundPrimary,
  width: isPanelOpen ? `${panelWidth}px` : 0,
  transition: `${transitionTime}ms ease-in-out`,
  height: '100%',
  overflow: 'hidden',
  zIndex: '1',
  borderTopLeftRadius: spacing['16px'],
  borderBottomLeftRadius: spacing['16px'],
  boxShadow: `0 2px 10px ${colors.shadowLow}`,
  padding: `${spacing['16px']} 0`,
  ...(panelContainerStyle ?? {}),
}))

const ChildrenContainer = styled.View<{ renderChild: boolean; transitionTime: number }>(
  ({ renderChild, transitionTime }) => ({
    opacity: renderChild ? 1 : 0,
    transition: `${transitionTime}ms ease-in-out`,
    height: '100%',
  }),
)

export type ExpandingSidePanelProps = {
  children: ReactElement
  iconColor?: string
  isPanelOpen: boolean
  setIsPanelOpen?: () => void
  tabColor?: string
  icon?: ReactElement
  panelWidth?: number
  panelContainerStyle?: CSSObject
  showsTab?: boolean
  transitionTime?: number
}

export const ExpandingSidePanel = ({
  children,
  icon,
  iconColor,
  isPanelOpen,
  setIsPanelOpen,
  tabColor,
  panelWidth = 500,
  panelContainerStyle,
  showsTab = true,
  transitionTime = 1000,
}: ExpandingSidePanelProps) => {
  const { colors } = useTheme()
  const [renderChild, setRenderChild] = useState(false)
  const [hoverOnTab, setHoverOnTab] = useState(false)
  const { formatMessage } = useIntl()
  useEffect(() => {
    if (isPanelOpen) {
      const timer = setTimeout(() => {
        setRenderChild(true)
      }, transitionTime)

      return () => clearTimeout(timer)
    } else {
      setRenderChild(false)
      return
    }
  }, [isPanelOpen, transitionTime])

  const getTabIcon = () => {
    if (hoverOnTab && isPanelOpen) {
      return (
        <ChevronIcon
          color={iconColor ?? colors.components.aiTab.text}
          direction={ChevronIconDirection.RIGHT}
          size={32}
        />
      )
    } else if (hoverOnTab && !isPanelOpen) {
      return (
        <ChevronIcon
          color={iconColor ?? colors.components.aiTab.text}
          direction={ChevronIconDirection.LEFT}
          size={32}
        />
      )
    } else {
      return icon ?? <SparklesIcon color={iconColor ?? colors.components.aiTab.text} size={'32'} />
    }
  }

  return (
    <Container testID={tID('ExpandingSidePanel-container')}>
      {showsTab && (
        <Tab
          onPress={setIsPanelOpen}
          isPanelOpen={isPanelOpen}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Side Panel Button',
            description: 'This button allows the user to expand the side panel.',
          })}
          tabColor={tabColor ?? colors.components.aiTab.background}
          testID={tID('ExpandingSidePanel-tab')}
          onHoverIn={() => setHoverOnTab(true)}
          onHoverOut={() => setHoverOnTab(false)}
        >
          {getTabIcon()}
        </Tab>
      )}
      <PanelContainer
        isPanelOpen={isPanelOpen}
        panelWidth={panelWidth}
        panelContainerStyle={panelContainerStyle}
        transitionTime={transitionTime}
        testID={tID('ExpandingSidePanel-panelContainer')}
      >
        <ChildrenContainer transitionTime={transitionTime} renderChild={renderChild}>
          {renderChild ? children : null}
        </ChildrenContainer>
      </PanelContainer>
    </Container>
  )
}
